"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState, type ComponentProps } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Carousel } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { cn } from "@repo/ui/lib/utils";
import { SanityMediaFieldContent } from "@repo/sanity-utils/components/media/SanityMediaFieldContent";
import type { DiscoveryPageQueryResult } from "@repo/sanity-utils/schema/types";
import type { ResolvedSanityImage } from "@/lib/discoveryMedia";
import styles from "./DiscoveryExpertBios.module.css";
type DiscoveryPageBlocks = NonNullable<NonNullable<DiscoveryPageQueryResult>["pageContent"]>;
export type DiscoveryExpertBiosBlock = Extract<DiscoveryPageBlocks[number], {
  _type: "discoveryExpertBios";
}>;
export type DiscoveryExpertBioItem = NonNullable<NonNullable<DiscoveryExpertBiosBlock["bios"]>[number]>;
export type DiscoveryExpertBioMedia = {
  imageData?: ResolvedSanityImage;
  alt: string;
};
type DiscoveryExpertBiosProps = {
  block: DiscoveryExpertBiosBlock;
  mediaById: Record<string, DiscoveryExpertBioMedia>;
};
type EmblaApi = Parameters<NonNullable<ComponentProps<typeof Carousel>["getEmblaApi"]>>[0];
const EMPTY_BIOS: DiscoveryExpertBioItem[] = [];
function ExpertBioCard(t0) {
  const $ = _c(17);
  const {
    bio,
    media
  } = t0;
  let t1;
  if ($[0] !== bio.name) {
    t1 = bio.name?.trim();
    $[0] = bio.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  if (!name) {
    return null;
  }
  let t2;
  if ($[2] !== bio.bio) {
    t2 = bio.bio?.trim() ?? "";
    $[2] = bio.bio;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const bioText = t2;
  const imageData = media?.imageData;
  const alt = media?.alt || `Photo of ${name}`;
  let t3;
  if ($[4] !== alt || $[5] !== imageData) {
    t3 = <div className={styles.imageWrap}>{imageData ? <div className={styles.imageFrame}><SanityMediaFieldContent image={{
          src: imageData.url,
          alt,
          width: imageData.width,
          height: imageData.height
        }} imageClassName={styles.image} sizes="(max-width: 799px) 300px, 320px" fill={true} /></div> : <div className={styles.imagePlaceholder} aria-hidden={true} />}</div>;
    $[4] = alt;
    $[5] = imageData;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== name) {
    t4 = <Typography as="h3" variant="h5" className={styles.name}>{name}</Typography>;
    $[7] = name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== bioText) {
    t5 = bioText ? <Typography as="p" variant="body" className={styles.bio}>{bioText}</Typography> : null;
    $[9] = bioText;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className={styles.cardBody}>{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <article className={styles.card}>{t3}{t6}</article>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function DiscoveryExpertBios(t0) {
  const $ = _c(58);
  const {
    block,
    mediaById
  } = t0;
  const [embla, setEmbla] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const bios = Array.isArray(block.bios) ? block.bios : EMPTY_BIOS;
  let t1;
  if ($[0] !== block.title) {
    t1 = block.title?.trim();
    $[0] = block.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== block.eyebrow) {
    t2 = block.eyebrow?.trim();
    $[2] = block.eyebrow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const eyebrow = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = api => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const syncControls = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = api_0 => {
      setEmbla(api_0);
      syncControls(api_0);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleEmblaApi = t4;
  let t5;
  let t6;
  if ($[6] !== embla) {
    t5 = () => {
      if (!embla) {
        return;
      }
      const onSelect = () => syncControls(embla);
      embla.on("select", onSelect);
      embla.on("reInit", onSelect);
      return () => {
        embla.off("select", onSelect);
        embla.off("reInit", onSelect);
      };
    };
    t6 = [embla, syncControls];
    $[6] = embla;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  if (!title || bios.length === 0) {
    return null;
  }
  let t7;
  if ($[9] !== eyebrow) {
    t7 = eyebrow ? <Typography variant="eyebrow" as="p" className={styles.eyebrow}>{eyebrow}</Typography> : null;
    $[9] = eyebrow;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t10;
  let t8;
  let t9;
  if ($[11] !== title) {
    t8 = <Typography as="h2" variant="h2" className={styles.titleDesktop}>{title}</Typography>;
    t9 = <Typography as="h2" variant="h3" className={styles.titleTablet}>{title}</Typography>;
    t10 = <Typography as="h2" variant="h4" className={styles.titleMobile}>{title}</Typography>;
    $[11] = title;
    $[12] = t10;
    $[13] = t8;
    $[14] = t9;
  } else {
    t10 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t11 = <header className={styles.header}>{t7}{t8}{t9}{t10}</header>;
    $[15] = t10;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      slide: styles.slide
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      align: "start",
      dragFree: false
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== bios || $[23] !== mediaById) {
    let t15;
    if ($[25] !== mediaById) {
      t15 = bio => <Carousel.Slide key={bio._id}><ExpertBioCard bio={bio} media={mediaById[bio._id]} /></Carousel.Slide>;
      $[25] = mediaById;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    t14 = bios.map(t15);
    $[22] = bios;
    $[23] = mediaById;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== title) {
    t15 = <Carousel classNames={t12} slideSize="var(--expert-card-width)" slideGap={0} emblaOptions={t13} withControls={false} withIndicators={false} getEmblaApi={handleEmblaApi} aria-label={title}>{t14}</Carousel>;
    $[27] = t14;
    $[28] = title;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = !canScrollPrev && styles.navButtonDisabled;
  let t17;
  if ($[30] !== t16) {
    t17 = cn(styles.navButton, t16);
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== embla) {
    t18 = () => embla?.scrollPrev();
    $[32] = embla;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = !canScrollPrev;
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ArrowLeft aria-hidden={true} size={24} strokeWidth={1.5} />;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t17 || $[36] !== t18 || $[37] !== t19) {
    t21 = <button type="button" className={t17} onClick={t18} disabled={t19} aria-label="Previous expert">{t20}</button>;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = !canScrollNext && styles.navButtonDisabled;
  let t23;
  if ($[39] !== t22) {
    t23 = cn(styles.navButton, t22);
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== embla) {
    t24 = () => embla?.scrollNext();
    $[41] = embla;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = !canScrollNext;
  let t26;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ArrowRight aria-hidden={true} size={24} strokeWidth={1.5} />;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t23 || $[45] !== t24 || $[46] !== t25) {
    t27 = <button type="button" className={t23} onClick={t24} disabled={t25} aria-label="Next expert">{t26}</button>;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t21 || $[49] !== t27) {
    t28 = <div className={styles.controls}>{t21}{t27}</div>;
    $[48] = t21;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t15 || $[52] !== t28) {
    t29 = <div className={styles.carousel}>{t15}{t28}</div>;
    $[51] = t15;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t11 || $[55] !== t29 || $[56] !== title) {
    t30 = <section className={styles.section} aria-label={title}>{t11}{t29}</section>;
    $[54] = t11;
    $[55] = t29;
    $[56] = title;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  return t30;
}